import {
  Box,
  Button,
  Card,
  Collapsible,
  Heading,
  HStack,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useNavigate } from '@tanstack/react-router'
import { AlertTriangle, ArrowLeft, CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import type { IcpData } from '@/data/business-value'
import { SectionHeading } from '~/components/shell'
import AnimatedCounter from './AnimatedCounter'
import CustomerStoryCard from './CustomerStoryCard'
import FadeInSection from './FadeInSection'
import HeroBanner from './HeroBanner'
import MetricCard from './MetricCard'
import ValuePropositionCard from './ValuePropositionCard'

interface IcpDetailPageProps {
  icp: IcpData
}

// This component owns its own top-of-page treatment (back button + HeroBanner)
// rather than a shell PageHeader: the four route wrappers that render it
// (data-centers/energy/logistics/smart-manufacturing) are thin pass-throughs
// with no header of their own, so the hero here fills that role.
export default function IcpDetailPage({ icp }: IcpDetailPageProps) {
  const navigate = useNavigate()
  const [expandedOutcomes, setExpandedOutcomes] = useState<Record<number, boolean>>({})

  const toggleOutcome = (index: number) => {
    setExpandedOutcomes((prev) => ({ ...prev, [index]: !prev[index] }))
  }

  return (
    <Box maxW="1400px" mx="auto">
      <VStack gap={10} align="stretch">
        {/* Back button */}
        <Button
          variant="ghost"
          alignSelf="flex-start"
          onClick={() => navigate({ to: '/business-value' })}
        >
          <HStack gap={2}>
            <ArrowLeft size={16} />
            <span>Back to Business Value</span>
          </HStack>
        </Button>

        {/* Hero Banner */}
        <FadeInSection>
          <HeroBanner icp={icp} tags={icp.valuePropositions.map((vp) => vp.title)} />
        </FadeInSection>

        {/* Key Metrics */}
        <VStack gap={4} align="stretch">
          <SectionHeading title="Key Metrics" />
          <FadeInSection delay={0.1}>
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={4}>
              {icp.keyMetrics.map((metric, i) => (
                <FadeInSection key={metric.label} delay={0.1 * i}>
                  <MetricCard metric={metric} delay={0.3 + 0.1 * i} />
                </FadeInSection>
              ))}
            </SimpleGrid>
          </FadeInSection>
        </VStack>

        {/* Challenges & Solutions */}
        <VStack gap={4} align="stretch">
          <SectionHeading title="Challenges & Solutions" />
          <FadeInSection delay={0.1}>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
              {/* Challenges */}
              <Card.Root>
                <Card.Body p={6}>
                  <VStack gap={4} align="start">
                    <HStack gap={2}>
                      <AlertTriangle size={20} color="var(--chakra-colors-orange-500)" />
                      <Heading size="sm">Industry Challenges</Heading>
                    </HStack>
                    <VStack gap={3} align="start" w="100%">
                      {icp.challenges.map((challenge, i) => (
                        <HStack key={challenge} gap={3} align="start">
                          <Text
                            fontWeight="bold"
                            color="content.secondary"
                            fontSize="sm"
                            minW={5}
                            textAlign="right"
                          >
                            {i + 1}.
                          </Text>
                          <Text fontSize="sm">{challenge}</Text>
                        </HStack>
                      ))}
                    </VStack>
                  </VStack>
                </Card.Body>
              </Card.Root>

              {/* Solutions */}
              <Card.Root>
                <Card.Body p={6}>
                  <VStack gap={4} align="start">
                    <HStack gap={2}>
                      <CheckCircle2 size={20} color="var(--chakra-colors-green-500)" />
                      <Heading size="sm">HiveMQ Solution</Heading>
                    </HStack>
                    <VStack gap={3} align="start" w="100%">
                      {icp.hivemqSolution.map((solution) => (
                        <HStack key={solution} gap={3} align="start">
                          <Box color="green.500" flexShrink={0} mt={0.5}>
                            <CheckCircle2 size={16} />
                          </Box>
                          <Text fontSize="sm">{solution}</Text>
                        </HStack>
                      ))}
                    </VStack>
                  </VStack>
                </Card.Body>
              </Card.Root>
            </SimpleGrid>
          </FadeInSection>
        </VStack>

        {/* Value Propositions */}
        <VStack gap={4} align="stretch">
          <SectionHeading title="Value Propositions" />
          <FadeInSection delay={0.1}>
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={4}>
              {icp.valuePropositions.map((vp, i) => (
                <FadeInSection key={vp.title} delay={0.1 * i}>
                  <ValuePropositionCard proposition={vp} color={icp.color} />
                </FadeInSection>
              ))}
            </SimpleGrid>
          </FadeInSection>
        </VStack>

        {/* Business Outcomes */}
        <VStack gap={4} align="stretch">
          <SectionHeading title="Business Outcomes" />
          <FadeInSection delay={0.1}>
            <VStack gap={4} align="stretch">
              {icp.businessOutcomes.map((outcome, i) => (
                <Card.Root key={outcome.title} overflow="hidden">
                  <Box
                    p={5}
                    cursor="pointer"
                    onClick={() => toggleOutcome(i)}
                    _hover={{ bg: 'bg.subtle' }}
                    transition="background 0.2s"
                  >
                    <HStack justify="space-between" w="100%">
                      <VStack gap={1} align="start">
                        <Heading size="sm">{outcome.title}</Heading>
                        <Text fontSize="sm" color="content.secondary">
                          {outcome.description}
                        </Text>
                      </VStack>
                      {expandedOutcomes[i] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </HStack>
                  </Box>
                  <Collapsible.Root open={!!expandedOutcomes[i]}>
                    <Collapsible.Content>
                      <Box px={5} pb={5} borderTop="1px solid" borderColor="border">
                        <SimpleGrid columns={{ base: 1, sm: 2 }} gap={4} pt={4}>
                          {outcome.metrics.map((metric) => (
                            <Card.Root key={metric.label} variant="outline">
                              <Card.Body p={4}>
                                <VStack gap={1} align="center">
                                  <AnimatedCounter
                                    value={metric.value}
                                    prefix={metric.prefix}
                                    suffix={metric.suffix}
                                    className="text-2xl font-bold"
                                  />
                                  <Text fontWeight="semibold" fontSize="sm">
                                    {metric.label}
                                  </Text>
                                  <Text fontSize="xs" color="content.secondary" textAlign="center">
                                    {metric.description}
                                  </Text>
                                </VStack>
                              </Card.Body>
                            </Card.Root>
                          ))}
                        </SimpleGrid>
                      </Box>
                    </Collapsible.Content>
                  </Collapsible.Root>
                </Card.Root>
              ))}
            </VStack>
          </FadeInSection>
        </VStack>

        {/* Customer Stories */}
        <VStack gap={4} align="stretch">
          <SectionHeading title="Customer Stories" />
          <FadeInSection delay={0.1}>
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={4}>
              {icp.customerStories.map((story, i) => (
                <FadeInSection key={story.name} delay={0.1 * i}>
                  <CustomerStoryCard story={story} color={icp.color} />
                </FadeInSection>
              ))}
            </SimpleGrid>
          </FadeInSection>
        </VStack>

        {/* CTA */}
        <FadeInSection delay={0.1}>
          <Card.Root
            bgGradient="to-r"
            gradientFrom={icp.gradient.from}
            gradientTo={icp.gradient.to}
            color="white"
            overflow="hidden"
          >
            <Card.Body p={8}>
              <VStack gap={4} align="center" textAlign="center">
                <Heading size="lg">{icp.ctaTitle}</Heading>
                <Text fontSize="md" opacity={0.9} maxW="2xl">
                  {icp.ctaDescription}
                </Text>
                <Button
                  size="lg"
                  bg="white"
                  color="gray.800"
                  _hover={{ bg: 'gray.100' }}
                  onClick={() => navigate({ to: icp.ctaRoute })}
                  mt={2}
                >
                  Launch Demo
                </Button>
              </VStack>
            </Card.Body>
          </Card.Root>
        </FadeInSection>
      </VStack>
    </Box>
  )
}
